import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { NEXUS_LOGO } from "../lib/api";
import api from "../lib/api";
import PushBell from "./PushBell";
import { SubscriptionBadge } from "./SubscriptionTierCard";
import {
  LayoutDashboard, Compass, Megaphone, Handshake, MessageSquare, Package, Wallet as WalletIcon,
  BarChart3, Star, Bell, Settings, Menu, X, LogOut, ChevronLeft, ClipboardCheck, Sparkles,
  ShieldCheck, Gavel, Gift, Users, UserCog, Wand2, Ticket, Trophy,
  UserPlus, Link2,
} from "lucide-react";

const ITEMS = [
  { to: "/brand", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/discover", label: "Discover Creators", icon: Compass },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone },
  { to: "/brand/applications", label: "Applications", icon: Handshake },
  { to: "/brand/approvals", label: "Approvals", icon: ClipboardCheck },
  { to: "/messages", label: "Messages", icon: MessageSquare },
  { to: "/groups", label: "Group chats", icon: Users },
  { to: "/brand/orders", label: "Orders", icon: Package },
  { to: "/brand/affiliates", label: "Affiliates", icon: Link2 },
  { to: "/brand/payments", label: "Payments", icon: WalletIcon },
  { to: "/brand/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/brand/saved", label: "Saved Creators", icon: Star },
  { to: "/brand/billing", label: "Billing", icon: Sparkles },
  { to: "/brand/notifications", label: "Notifications", icon: Bell },
  { to: "/brand/settings", label: "Settings", icon: Settings },
  { to: "/settings", label: "Account", icon: UserCog },
];

const ADMIN_ITEMS = [
  { to: "/admin/disputes", label: "Disputes", icon: Gavel },
  { to: "/admin/identity", label: "Identity", icon: ShieldCheck },
  { to: "/admin/showcase", label: "Results wall", icon: Trophy },
  { to: "/admin/broadcast", label: "Message users", icon: Megaphone },
  { to: "/admin/leads", label: "Invite leads", icon: UserPlus },
  { to: "/admin/partners", label: "Partners", icon: Handshake },
  { to: "/admin/ad-queries", label: "Ad spend queries", icon: Megaphone },
  { to: "/admin/signups", label: "Signups", icon: Users },
  { to: "/admin/studio", label: "Studio", icon: Wand2 },
  { to: "/admin/promo", label: "Promo codes", icon: Ticket },
  { to: "/admin/raffle", label: "Raffle", icon: Gift },
  { to: "/admin/payouts", label: "Payouts", icon: ShieldCheck },
];

export default function BrandShell({ children, title }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    api.get("/admin/me").then((r) => setIsAdmin(!!r.data?.is_admin)).catch(() => setIsAdmin(false));
  }, [user]);
  const allItems = isAdmin ? [...ITEMS, ...ADMIN_ITEMS] : ITEMS;
  const activeLabel = allItems.find((i) => (i.end ? loc.pathname === i.to : loc.pathname.startsWith(i.to)))?.label || title;
  return (
    <div className="min-h-screen grain flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#0A0A0E] border-r border-white/5 flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`} data-testid="brand-sidebar">
        <div className="p-5 flex items-center gap-2.5 border-b border-white/5">
          <img src={NEXUS_LOGO} alt="" className="h-7 w-7" />
          <div className="min-w-0">
            <div className="font-display font-bold tracking-tight leading-none">NEXUS</div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">Brand Portal</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {user && <SubscriptionBadge compact />}
            <button onClick={() => setOpen(false)} className="lg:hidden text-zinc-400 hover:text-white" aria-label="Close menu"><X className="h-4 w-4" /></button>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1" data-testid="brand-nav">
          {ITEMS.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                onClick={() => setOpen(false)}
                data-testid={`brand-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-white/[0.06] text-white nexus-border-glow" : "text-zinc-400 hover:bg-white/[0.03] hover:text-white"}`}
              >
                <Icon className="h-4 w-4" />
                <span>{it.label}</span>
              </NavLink>
            );
          })}
          {isAdmin && (
            <div className="pt-4 mt-4 border-t border-white/5" data-testid="admin-nav-section">
              <div className="px-3 pb-2 text-[9px] uppercase tracking-widest text-nexus-pink/70 flex items-center gap-1.5">
                <ShieldCheck className="h-3 w-3" /> Admin
              </div>
              {ADMIN_ITEMS.map((it) => {
                const Icon = it.icon;
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    onClick={() => setOpen(false)}
                    data-testid={`brand-nav-admin-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-nexus-pink/10 text-white nexus-border-glow" : "text-zinc-400 hover:bg-white/[0.03] hover:text-white"}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{it.label}</span>
                  </NavLink>
                );
              })}
            </div>
          )}
        </nav>
        <div className="p-3 border-t border-white/5">
          <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-zinc-400 hover:bg-white/[0.03] hover:text-white transition-colors" data-testid="brand-nav-logout">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </aside>
      {open && <div className="lg:hidden fixed inset-0 bg-black/60 z-30" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden h-14 flex items-center gap-3 px-4 border-b border-white/5 bg-[#060608]/90 backdrop-blur pt-[env(safe-area-inset-top)] box-content">
          <button onClick={() => setOpen(true)} className="text-zinc-300" aria-label="Open menu" data-testid="brand-menu-btn">
            <Menu className="h-5 w-5" />
          </button>
          <div className="font-display font-bold">{activeLabel}</div>
          <div className="ml-auto flex items-center gap-2">
            {user && <SubscriptionBadge compact />}
            {user && <PushBell compact />}
          </div>
        </header>
        <main className="flex-1 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}

export function BrandBackLink({ to, label = "Back" }) {
  return (
    <NavLink to={to} className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors mb-4">
      <ChevronLeft className="h-4 w-4" /> {label}
    </NavLink>
  );
}
